import { Col } from "antd";
import { Form, SubmitButton, Select } from "formik-antd";
import { useTranslation } from "react-i18next";
import { integrationService } from "/app/src/services";
import { WorkflowAction, Report, Integration } from "/app/src/models";
import { buildParams } from "/app/src/helpers/params";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@heroui/react";
import { useCallback } from "react";

/**
 * Renders a CSV selection form and handles workflow actions.
 *
 * @param {object} param - The properties object.
 * @param {Report[]} param.reports - Array of reports available for selection.
 * @param {boolean} param.isNew - Flag indicating whether the CSV is new.
 * @param {(action: WorkflowAction) => void} [param.removeWorkflowAction] - Function to remove a workflow action.
 * @param {boolean} param.dirty - Flag indicating if the form has unsaved changes.
 * @param {number | undefined} param.appId - The application ID.
 * @param {WorkflowAction} [param.action] - The workflow action object.
 * @returns {JSX.Element} The rendered CSV form component.
 */
export default function CSV({
  reports,
  isNew,
  removeWorkflowAction,
  dirty,
  appId,
  action,
}: {
  reports: Report[];
  isNew: boolean;
  removeWorkflowAction?: (action: WorkflowAction) => void;
  dirty: boolean;
  action?: WorkflowAction;
  appId: number | undefined;
}) {
  const { t } = useTranslation();
  const handleClick = useCallback(() => {
    if (removeWorkflowAction && action) {
      removeWorkflowAction(action);
    }
  }, [removeWorkflowAction, action]);

  const { data: integrations } = useQuery({
    queryKey: ["integrationsByApp", appId],
    queryFn: () => {
      return integrationService.getAll(buildParams({ appId }));
    },
    enabled: Boolean(appId),
    initialData: { integrations: [] },
    select: (data: { integrations: Integration[] }) => {
      return data.integrations;
    },
  });

  return (
    <>
      <Col span={7}>
        <Form.Item name="reportId">
          <Select
            name="reportId"
            size="large"
            placeholder={t("translation:select_report")}
          >
            {reports.map((c) => (
              <Select.Option key={c.id} value={c.id}>
                {c.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={5}>
        <Form.Item name="integrationId">
          <Select
            name="integrationId"
            size="large"
            placeholder={t("translation:select_delimiter")}
          >
            {integrations.map((c) => (
              <Select.Option key={c.id} value={c.id}>
                {c.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={5}>
        <SubmitButton type="primary" size="large" block disabled={!dirty}>
          {isNew ? t("translation:add_action") : t("translation:save")}
        </SubmitButton>
      </Col>
      {!isNew && (
        <Col span={3}>
          <Button
            size="md"
            radius="none"
            variant="bordered"
            color="default"
            fullWidth
            onPress={handleClick}
            className="hover:border-primary-default hover:text-primary-default bg-white"
          >
            {t("translation:remove")}
          </Button>
        </Col>
      )}
    </>
  );
}
